// frontend/src/lib/supabase.ts
// Type definitions and constants only - NO Supabase client

export type SubscriptionTier = 'free' | 'standard_with_ads' | 'standard' | 'premium';

// Monetization mode for mini-apps
export type MonetizationMode = 'free' | 'free_with_paid_features' | 'paid_only';

// Mini-app category
export type MiniAppType = 'game' | 'utility';

export type Database = {
  public: {
    Tables: {
      profiles: {
        Row: {
          id: string;
          email: string;
          display_name: string | null;
          username: string | null;
          avatar_url: string | null;
          anthropic_api_key: string | null;
          kimi_api_key: string | null;
          glm_api_key: string | null;
          minimax_api_key: string | null;
          stripe_account_id: string | null;
          stripe_onboarding_completed: boolean;
          stripe_charges_enabled: boolean;
          country: string | null;
          is_admin: boolean;
          is_public_profile: boolean;
          auto_submit_errors: boolean;
          // Subscription fields (Stage 1)
          subscription_tier: SubscriptionTier;
          subscription_expires_at: string | null;
          stripe_customer_id: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id: string;
          email: string;
          display_name?: string | null;
          username?: string | null;
          avatar_url?: string | null;
          anthropic_api_key?: string | null;
          kimi_api_key?: string | null;
          glm_api_key?: string | null;
          minimax_api_key?: string | null;
          stripe_account_id?: string | null;
          stripe_onboarding_completed?: boolean;
          stripe_charges_enabled?: boolean;
          country?: string | null;
          is_admin?: boolean;
          is_public_profile?: boolean;
          auto_submit_errors?: boolean;
          subscription_tier?: SubscriptionTier;
          subscription_expires_at?: string | null;
          stripe_customer_id?: string | null;
        };
        Update: {
          display_name?: string | null;
          username?: string | null;
          avatar_url?: string | null;
          anthropic_api_key?: string | null;
          kimi_api_key?: string | null;
          glm_api_key?: string | null;
          minimax_api_key?: string | null;
          stripe_account_id?: string | null;
          stripe_onboarding_completed?: boolean;
          stripe_charges_enabled?: boolean;
          country?: string | null;
          is_admin?: boolean;
          is_public_profile?: boolean;
          auto_submit_errors?: boolean;
          subscription_tier?: SubscriptionTier;
          subscription_expires_at?: string | null;
          stripe_customer_id?: string | null;
        };
      };
      projects: {
        Row: {
          id: string;
          user_id: string;
          name: string;
          description: string | null;
          specification: string | null;
          logo_emoji: string | null;
          type: MiniAppType | null;
          admin_type_override: MiniAppType | null;
          is_published: boolean;
          is_promoted: boolean;
          slug: string | null;
          public_id: string | null;
          cloud_run_job_name: string | null;
          cloud_run_service_url: string | null;
          cloud_run_cpu: string | null;
          cloud_run_memory: string | null;
          build_status: string | null;
          last_build_id: string | null;
          last_build_at: string | null;
          // Feature flags (Stage 1)
          requires_ai: boolean;
          requires_storage: boolean;
          is_premium: boolean;
          // Tier gating (Stage 5)
          required_tier: SubscriptionTier | null;
          // Monetization mode (replaces simple paid toggle)
          monetization_mode: MonetizationMode;
          // Whether mini-app requires authentication (based on code analysis)
          requires_auth: boolean;
          // Admin-only: allow non-authenticated users to use this app (including AI features)
          allow_guest_access: boolean;
          // Forking
          forked_from: string | null;
          is_family_template: boolean;
          family_creation_prompt: string | null;
          family_params: Record<string, string> | null;
          family_config_ref: string | null;
          is_family_customized: boolean;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          user_id: string;
          name: string;
          description?: string | null;
          specification?: string | null;
          logo_emoji?: string | null;
          type?: MiniAppType | null;
          admin_type_override?: MiniAppType | null;
          is_published?: boolean;
          is_promoted?: boolean;
          requires_ai?: boolean;
          requires_storage?: boolean;
          is_premium?: boolean;
          required_tier?: SubscriptionTier | null;
          monetization_mode?: MonetizationMode;
          requires_auth?: boolean;
          allow_guest_access?: boolean;
          cloud_run_cpu?: string | null;
          cloud_run_memory?: string | null;
          forked_from?: string | null;
          is_family_template?: boolean;
          family_creation_prompt?: string | null;
          family_params?: Record<string, string> | null;
          family_config_ref?: string | null;
          is_family_customized?: boolean;
        };
        Update: {
          name?: string;
          description?: string | null;
          specification?: string | null;
          logo_emoji?: string | null;
          type?: MiniAppType | null;
          admin_type_override?: MiniAppType | null;
          is_published?: boolean;
          is_promoted?: boolean;
          requires_ai?: boolean;
          requires_storage?: boolean;
          is_premium?: boolean;
          required_tier?: SubscriptionTier | null;
          monetization_mode?: MonetizationMode;
          requires_auth?: boolean;
          allow_guest_access?: boolean;
          cloud_run_cpu?: string | null;
          cloud_run_memory?: string | null;
          is_family_template?: boolean;
          family_creation_prompt?: string | null;
          family_params?: Record<string, string> | null;
          family_config_ref?: string | null;
          is_family_customized?: boolean;
        };
      };
      comments: {
        Row: {
          id: string;
          project_public_id: string;
          user_id: string;
          parent_id: string | null;
          content: string;
          mentions: { id: string; username: string }[];
          upvotes: string[];
          downvotes: string[];
          created_at: string;
          updated_at: string;
          deleted_at: string | null;
        };
        Insert: {
          project_public_id: string;
          user_id: string;
          parent_id?: string | null;
          content: string;
          mentions?: { id: string; username: string }[];
        };
        Update: {
          content?: string;
          mentions?: { id: string; username: string }[];
          deleted_at?: string | null;
          updated_at?: string;
        };
      };
      conversations: {
        Row: {
          id: string;
          project_id: string;
          title: string;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          project_id: string;
          title?: string;
        };
        Update: {
          title?: string;
        };
      };
      messages: {
        Row: {
          id: string;
          conversation_id: string;
          role: 'user' | 'assistant' | 'system';
          content: string;
          created_at: string;
        };
        Insert: {
          conversation_id: string;
          role: 'user' | 'assistant' | 'system';
          content: string;
        };
        Update: never;
      };
      message_attachments: {
        Row: {
          id: string;
          message_id: string;
          file_name: string;
          mime_type: string;
          file_size: number;
          storage_path: string;
          created_at: string;
        };
        Insert: {
          message_id: string;
          file_name: string;
          mime_type: string;
          file_size: number;
          storage_path: string;
        };
        Update: never;
      };
      project_files: {
        Row: {
          id: string;
          project_id: string;
          original_filename: string;
          storage_path: string;
          file_type: 'upload' | 'result';
          file_size: number | null;
          status: 'pending' | 'processing' | 'completed' | 'failed';
          error_message: string | null;
          related_upload_id: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          project_id: string;
          original_filename: string;
          storage_path: string;
          file_type: 'upload' | 'result';
          file_size?: number | null;
          status?: 'pending' | 'processing' | 'completed' | 'failed';
          error_message?: string | null;
          related_upload_id?: string | null;
        };
        Update: {
          status?: 'pending' | 'processing' | 'completed' | 'failed';
          error_message?: string | null;
        };
      };
      app_settings: {
        Row: {
          id: string;
          creator_features_enabled: boolean;
          updated_at: string;
        };
        Insert: never; // Single row, never insert from frontend
        Update: {
          creator_features_enabled?: boolean;
        };
      };
      app_usage: {
        Row: {
          id: string;
          project_id: string;
          user_id: string;
          created_at: string;
        };
        Insert: {
          project_id: string;
          user_id: string;
        };
        Update: never;
      };
      // New table: User app data storage (Stage 1)
      app_user_data: {
        Row: {
          id: string;
          project_id: string;
          user_id: string;
          collection: string;
          record_id: string;
          data: Record<string, unknown>;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          project_id: string;
          user_id: string;
          collection: string;
          record_id: string;
          data: Record<string, unknown>;
        };
        Update: {
          data?: Record<string, unknown>;
        };
      };
      // New table: Usage limits tracking (Stage 1)
      usage_limits: {
        Row: {
          user_id: string;
          ai_calls_today: number;
          ai_calls_month: number;
          storage_bytes_used: number;
          last_reset_daily: string;
          last_reset_monthly: string;
          updated_at: string;
        };
        Insert: {
          user_id: string;
          ai_calls_today?: number;
          ai_calls_month?: number;
          storage_bytes_used?: number;
        };
        Update: {
          ai_calls_today?: number;
          ai_calls_month?: number;
          storage_bytes_used?: number;
        };
      };
      // Revenue sharing tables (Stage 2)
      revenue_periods: {
        Row: {
          id: string;
          period_month: string;
          total_revenue_cents: number;
          platform_share_cents: number;
          creator_pool_cents: number;
          total_app_runs: number;
          status: 'pending' | 'calculated' | 'paid';
          created_at: string;
          calculated_at: string | null;
          paid_at: string | null;
        };
        Insert: {
          period_month: string;
          total_revenue_cents?: number;
          platform_share_cents?: number;
          creator_pool_cents?: number;
          total_app_runs?: number;
          status?: 'pending' | 'calculated' | 'paid';
        };
        Update: {
          total_revenue_cents?: number;
          platform_share_cents?: number;
          creator_pool_cents?: number;
          total_app_runs?: number;
          status?: 'pending' | 'calculated' | 'paid';
          calculated_at?: string;
          paid_at?: string;
        };
      };
      creator_earnings: {
        Row: {
          id: string;
          period_id: string;
          creator_id: string;
          app_runs: number;
          earnings_cents: number;
          payout_status: 'pending' | 'processing' | 'paid' | 'failed' | 'below_minimum';
          stripe_transfer_id: string | null;
          created_at: string;
          paid_at: string | null;
        };
        Insert: {
          period_id: string;
          creator_id: string;
          app_runs?: number;
          earnings_cents?: number;
          payout_status?: 'pending' | 'processing' | 'paid' | 'failed' | 'below_minimum';
        };
        Update: {
          app_runs?: number;
          earnings_cents?: number;
          payout_status?: 'pending' | 'processing' | 'paid' | 'failed' | 'below_minimum';
          stripe_transfer_id?: string;
          paid_at?: string;
        };
      };
      payouts: {
        Row: {
          id: string;
          creator_id: string;
          period_id: string | null;
          amount_cents: number;
          stripe_account_id: string;
          stripe_transfer_id: string | null;
          status: 'pending' | 'processing' | 'succeeded' | 'failed';
          error_message: string | null;
          created_at: string;
          processed_at: string | null;
        };
        Insert: {
          creator_id: string;
          period_id?: string;
          amount_cents: number;
          stripe_account_id: string;
          status?: 'pending' | 'processing' | 'succeeded' | 'failed';
        };
        Update: {
          stripe_transfer_id?: string;
          status?: 'pending' | 'processing' | 'succeeded' | 'failed';
          error_message?: string;
          processed_at?: string;
        };
      };
      code_validations: {
        Row: {
          id: string;
          project_id: string;
          conversation_id: string | null;
          validation_result: 'no_change_needed' | 'fixed';
          validated_at: string;
          created_at: string;
        };
        Insert: {
          project_id: string;
          conversation_id?: string | null;
          validation_result: 'no_change_needed' | 'fixed';
          validated_at?: string;
        };
        Update: {
          validation_result?: 'no_change_needed' | 'fixed';
          conversation_id?: string | null;
        };
      };
    };
    Functions: {
      get_app_storage_usage: {
        Args: { p_user_id: string; p_project_id: string };
        Returns: number;
      };
      get_total_storage_usage: {
        Args: { p_user_id: string };
        Returns: number;
      };
      increment_ai_usage: {
        Args: { p_user_id: string };
        Returns: void;
      };
      reset_daily_usage: {
        Args: Record<string, never>;
        Returns: void;
      };
      reset_monthly_usage: {
        Args: Record<string, never>;
        Returns: void;
      };
      calculate_creator_shares: {
        Args: { 
          p_period_month: string; 
          p_total_revenue_cents: number;
          p_creator_share_percent?: number;
        };
        Returns: Array<{
          creator_id: string;
          app_runs: number;
          share_percent: number;
          earnings_cents: number;
        }>;
      };
    };
  };
};

// Agent backlog status
export type AgentBacklogStatus = 'pending' | 'building' | 'success' | 'failed';

export type AgentBacklogItem = {
  id: string;
  title: string;
  description: string | null;
  status: AgentBacklogStatus;
  project_id: string | null;
  project_url: string | null;
  agent_log: Record<string, unknown>[] | null;
  screenshot_paths: string[] | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
};

export type Profile = Database['public']['Tables']['profiles']['Row'];
export type Project = Database['public']['Tables']['projects']['Row'];
export type Conversation = Database['public']['Tables']['conversations']['Row'];
export type Message = Database['public']['Tables']['messages']['Row'];
export type ProjectFile = Database['public']['Tables']['project_files']['Row'];
export type AppSettings = Database['public']['Tables']['app_settings']['Row'];
export type AppUsage = Database['public']['Tables']['app_usage']['Row'];
export type AppUserData = Database['public']['Tables']['app_user_data']['Row'];
export type UsageLimits = Database['public']['Tables']['usage_limits']['Row'];
export type RevenuePeriod = Database['public']['Tables']['revenue_periods']['Row'];
export type CreatorEarnings = Database['public']['Tables']['creator_earnings']['Row'];
export type Payout = Database['public']['Tables']['payouts']['Row'];
export type CodeValidation = Database['public']['Tables']['code_validations']['Row'];

// Credit costs
export const SPONSORED_AI_CALL_COST = 8;
export const BUILDER_AI_PER_30_SEC_COST = 4;

// Free tier credit constants (paid tier allowances are now in Stripe product metadata)
export const FREE_DAILY_CREDITS = 32;
export const FREE_MONTHLY_CAP = 100;
export const FREE_SIGNUP_BONUS = 64;
export const ANONYMOUS_DAILY_CREDITS = 16;

// Storage limits by tier (in bytes) - unchanged, not credit-based
export const STORAGE_LIMITS: Record<SubscriptionTier, number> = {
  free: 1 * 1024 * 1024,                   // 1 MB
  standard_with_ads: 5 * 1024 * 1024,      // 5 MB
  standard: 25 * 1024 * 1024,              // 25 MB
  premium: 100 * 1024 * 1024,              // 100 MB
};

// Max file size per message attachment and mini-app payload by tier
export const MESSAGE_ATTACHMENT_MAX_BYTES: Record<SubscriptionTier, number> = {
  free: 10 * 1024 * 1024,                  // 10 MB
  standard_with_ads: 20 * 1024 * 1024,     // 20 MB
  standard: 20 * 1024 * 1024,              // 20 MB
  premium: 64 * 1024 * 1024,               // 64 MB
};

// Max processing time per mini-app run by tier (in seconds)
export const MAX_PROCESSING_TIME_SECONDS: Record<SubscriptionTier, number> = {
  free: 10 * 60,                           // 10 minutes
  standard_with_ads: 60 * 60,              // 1 hour
  standard: 60 * 60,                       // 1 hour
  premium: 8 * 60 * 60,                    // 8 hours
};

// Credit pre-check threshold: after this many effective seconds of runtime,
// verify user has enough credits to cover the remaining max processing time
export const CREDIT_PRECHECK_THRESHOLD_SECONDS: Record<SubscriptionTier, number> = {
  free: 30,                                // 30 seconds
  standard_with_ads: 60,                   // 60 seconds
  standard: 60,                            // 60 seconds
  premium: 600,                            // 10 minutes
};

// Tier hierarchy for comparison (higher number = higher tier)
export const TIER_LEVELS: Record<SubscriptionTier, number> = {
  free: 0,
  standard_with_ads: 1,
  standard: 2,
  premium: 3,
};

/**
 * Check if a user has a paid subscription (any tier above free)
 */
export function isPaidTier(tier: SubscriptionTier): boolean {
  return tier !== 'free';
}

/**
 * Check if a user's tier meets the required tier for an app
 * @param userTier - The user's current subscription tier
 * @param requiredTier - The tier required by the app (null = free, any value = paid required)
 * @returns true if user has access
 */
export function hasAccessToTier(
  userTier: SubscriptionTier,
  requiredTier: SubscriptionTier | null
): boolean {
  if (!requiredTier) return true; // No requirement = free access
  // Any paid tier grants access to paid apps
  return isPaidTier(userTier);
}

/**
 * Check if a user can access a mini-app based on monetization mode
 * @param userTier - The user's current subscription tier
 * @param monetizationMode - The mini-app's monetization mode
 * @param isAuthenticated - Whether the user is authenticated
 * @returns { canAccess: boolean, reason?: string }
 */
export function canAccessMiniApp(
  userTier: SubscriptionTier | null,
  monetizationMode: MonetizationMode,
  isAuthenticated: boolean,
  requiresAuth: boolean
): { canAccess: boolean; reason?: 'auth_required' | 'subscription_required' } {
  // If the app requires authentication (uses AI, KV, etc.)
  if (requiresAuth && !isAuthenticated) {
    return { canAccess: false, reason: 'auth_required' };
  }

  // Check monetization mode
  switch (monetizationMode) {
    case 'free':
    case 'free_with_paid_features':
      // Free for all users (authenticated or not, depending on requiresAuth)
      return { canAccess: true };
    
    case 'paid_only':
      // Only paid subscribers can use this app
      if (!isAuthenticated) {
        return { canAccess: false, reason: 'auth_required' };
      }
      if (!userTier || !isPaidTier(userTier)) {
        return { canAccess: false, reason: 'subscription_required' };
      }
      return { canAccess: true };
    
    default:
      return { canAccess: true };
  }
}

/**
 * Get display name for a monetization mode
 */
export function getMonetizationModeDisplayName(mode: MonetizationMode): string {
  const names: Record<MonetizationMode, string> = {
    free: 'Free for all users',
    free_with_paid_features: 'Free with premium features',
    paid_only: 'Paid subscribers only',
  };
  return names[mode];
}

/**
 * Get description for a monetization mode
 */
export function getMonetizationModeDescription(mode: MonetizationMode): string {
  const descriptions: Record<MonetizationMode, string> = {
    free: 'Anyone can use this app. Usage is tracked but not monetized.',
    free_with_paid_features: 'Free users can use basic features. Paid users get premium features. Revenue sharing enabled.',
    paid_only: 'Only paid subscribers can use this app. Revenue sharing enabled.',
  };
  return descriptions[mode];
}

/**
 * Get display name for a subscription tier
 */
export function getTierDisplayName(tier: SubscriptionTier | null): string {
  if (!tier) return 'Free';
  const names: Record<SubscriptionTier, string> = {
    free: 'Free',
    standard_with_ads: 'Standard with Ads',
    standard: 'Standard',
    premium: 'Premium',
  };
  return names[tier];
}
